// A face a team wears as its badge (PLAN-2026-10-24-the-face-maker, B-488):
// named parts, a color for each paintable area, and a few whole steps of
// adjustment, about half a kilobyte of JSON. The bots' faces are the same
// shape (`BOT_FACES`), so one renderer draws both.
//
// DATA, NOT DRAWINGS. The catalog is each part's id, its name aloud, the
// areas it can be painted in and the colors choosing it brings; how a part
// looks is the client's, as the badges' icons are. A second client draws the
// same parts its own way.
//
// A FIXED SET, so a family league never meets a face it would rather a child
// did not see: nothing is uploaded or drawn freehand, and color is the only
// free choice (DIRECTION U25).
//
// A WHOLE CHARACTER (PLAN-2026-10-24-faces-round-three §2): the same face on a
// small body, in an outfit, with a number on its jersey and something in its
// hand. Every field it adds is optional, so a face saved before still parses
// and draws as it did.

import { z } from 'zod';
import { TEAM_BANNERS, type BannerChoice } from './banners';

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

// ── The paintable areas ────────────────────────────────────────────────────

/**
 * The areas a face is painted in. Every region a client draws names one, so a
 * paint can fills exactly that area; the team's disc behind the face is the
 * team's own two colors and not a slot.
 */
export const FACE_SLOTS = [
  'skin',
  'hair',
  'brows',
  'facial',
  'eyes',
  'hat',
  'hatTrim',
  'frames',
  'lenses',
  'neck',
  'neckTrim',
  'paint',
] as const;
export type FaceSlot = (typeof FACE_SLOTS)[number];

/**
 * The character's areas (§2.1): its outfit and the outfit's trim (the number
 * is painted in the trim), its pants and shoes, and what it holds. Each is
 * optional in a face's colors, and an absent one is drawn in a color
 * `figureColors` resolves: the team's for the outfit, a near-black for the
 * shoes. (A backdrop's area was here until the backdrops became the team's
 * banner, PLAN-2026-10-24-team-banners.)
 */
export const FIGURE_SLOTS = ['shirt', 'shirtTrim', 'pants', 'shoes', 'held'] as const;
export type FigureSlot = (typeof FIGURE_SLOTS)[number];
/** Any area a face or its character is painted in. */
export type FaceArea = FaceSlot | FigureSlot;
/** Every area, the face's first. */
export const FACE_AREAS: readonly FaceArea[] = [...FACE_SLOTS, ...FIGURE_SLOTS];

/** What each area is called on screen and aloud. */
export const FACE_SLOT_NAMES: Readonly<Record<FaceArea, string>> = {
  skin: 'Skin',
  hair: 'Hair',
  brows: 'Brows',
  facial: 'Beard',
  eyes: 'Eyes',
  hat: 'Hat',
  hatTrim: 'Hat trim',
  frames: 'Frames',
  lenses: 'Lenses',
  neck: 'Neck',
  neckTrim: 'Neck trim',
  paint: 'Face paint',
  shirt: 'Outfit',
  shirtTrim: 'Outfit trim',
  pants: 'Pants',
  shoes: 'Shoes',
  held: 'In hand',
};

// ── The seasons ────────────────────────────────────────────────────────────

export type FaceSeasonId = 'valentines' | 'st-patricks' | 'halloween' | 'thanksgiving' | 'winter';

/**
 * A span of the calendar a seasonal part is offered in
 * (PLAN-2026-10-24-faces-round-two §2): a holiday is a date, not a week of the
 * football season, so it is read from the device's own date.
 */
export interface FaceSeason {
  readonly id: FaceSeasonId;
  /** Its name aloud and in a sentence: *for Halloween*. */
  readonly name: string;
  /** Its name on a part's tag in the maker, where there is little room. */
  readonly tag: string;
  /** Its first and last days, as month and day, both included. A season can run over New Year's. */
  readonly from: readonly [month: number, day: number];
  readonly to: readonly [month: number, day: number];
}

export const FACE_SEASONS: readonly FaceSeason[] = [
  { id: 'valentines', name: "Valentine's Day", tag: "Valentine's", from: [2, 1], to: [2, 15] },
  { id: 'st-patricks', name: "St. Patrick's Day", tag: "St. Pat's", from: [3, 10], to: [3, 18] },
  { id: 'halloween', name: 'Halloween', tag: 'Halloween', from: [10, 1], to: [11, 2] },
  { id: 'thanksgiving', name: 'Thanksgiving', tag: 'Thanksgiving', from: [11, 10], to: [11, 30] },
  { id: 'winter', name: 'the winter holidays', tag: 'Holidays', from: [12, 1], to: [1, 6] },
];

/** The seasons on a day, by the day's own month and date where it is read. */
export function faceSeasonsOn(on: Date): FaceSeasonId[] {
  const day = (on.getMonth() + 1) * 100 + on.getDate();
  return FACE_SEASONS.filter((s) => {
    const from = s.from[0] * 100 + s.from[1];
    const to = s.to[0] * 100 + s.to[1];
    return from <= to ? day >= from && day <= to : day >= from || day <= to;
  }).map((s) => s.id);
}

/** A season by id. */
export function faceSeason(id: FaceSeasonId): FaceSeason {
  return FACE_SEASONS.find((s) => s.id === id)!;
}

// ── The parts ──────────────────────────────────────────────────────────────

/** One choice in a category: its id, its name aloud, and what it is painted in. */
export interface FacePart {
  readonly id: string;
  readonly name: string;
  /** The areas this choice can be painted in; the maker shows a row of colors for each. */
  readonly slots: readonly FaceArea[];
  /** The colors choosing it brings, where it has colors of its own: a crown is gold. */
  readonly paints?: Readonly<Partial<Record<FaceArea, string>>>;
  /** Offered only in its season, and kept by a face that wears it after (`facePartOffered`). */
  readonly season?: FaceSeasonId;
  /**
   * The achievement that earns it, by its id in the achievements catalog
   * (PLAN-2026-10-24-faces-round-three §5): worn only by an account that has
   * earned it, and kept by a face that already wears it.
   */
  readonly earnedBy?: string;
  /** What earns it, as the maker says beside it while it is not earned: *Win a league's championship.* */
  readonly earnedHow?: string;
  /**
   * Kept drawable for a face that wears it and offered nowhere else
   * (FACES.md §2): the maker, Shuffle and a theme never choose it. The
   * garments the neck carried before there was a body are outfits now.
   */
  readonly retired?: true;
}

const S = <I extends string>(
  id: I,
  name: string,
  slots: readonly FaceArea[] = [],
  paints?: FacePart['paints'],
): FacePart & { readonly id: I } => ({ id, name, slots, ...(paints ? { paints } : {}) });

/** A part for a season. */
const seasonal = <P extends FacePart>(season: FaceSeasonId, part: P): P => ({ ...part, season });
/** A part kept for the faces that wear it, and offered no more. */
const retired = <P extends FacePart>(part: P): P => ({ ...part, retired: true });
/**
 * A part earned by playing (§5): the achievement that earns it, by its id in
 * the achievements catalog, and what earns it in the catalog's words.
 */
const earned = <P extends FacePart>(by: string, how: string, part: P): P => ({
  ...part,
  earnedBy: by,
  earnedHow: how,
});

/**
 * Whether the maker offers a part on a day: an everyday part always, a
 * seasonal one in its season, and any part a face already wears, so a witch's
 * hat chosen in October stays chosen, and shown, in December. A retired part
 * only to a face that wears it, and an earned one to its earner (`earned`,
 * the achievements they hold).
 */
export function facePartOffered(
  part: FacePart,
  on: Date,
  worn = false,
  earned: readonly string[] = [],
): boolean {
  if (part.retired) return worn;
  if (part.earnedBy !== undefined) return worn || earned.includes(part.earnedBy);
  return worn || part.season === undefined || faceSeasonsOn(on).includes(part.season);
}

export const FACE_HEADS = [
  S('round', 'Round', ['skin']),
  S('oval', 'Oval', ['skin']),
  S('square', 'Square', ['skin']),
  S('long', 'Long', ['skin']),
  S('heart', 'Heart', ['skin']),
  S('wide', 'Wide', ['skin']),
  S('robot', 'Robot', ['skin']),
] as const satisfies readonly FacePart[];

export const FACE_EARS = [
  S('none', 'None'),
  S('small', 'Small', ['skin']),
  S('round', 'Round', ['skin']),
  S('big', 'Big', ['skin']),
  S('pointy', 'Pointed', ['skin']),
  S('floppy', 'Floppy', ['skin']),
  S('bolts', 'Bolts'),
] as const satisfies readonly FacePart[];

export const FACE_HAIR = [
  S('none', 'None'),
  S('bald', 'Horseshoe', ['hair']),
  S('buzz', 'Buzz cut', ['hair']),
  S('short', 'Short', ['hair']),
  S('side', 'Side part', ['hair']),
  S('swoop', 'Swoop', ['hair']),
  S('spiky', 'Spiky', ['hair']),
  S('messy', 'Messy', ['hair']),
  S('curly', 'Curly', ['hair']),
  S('afro', 'Afro', ['hair']),
  S('puffs', 'Puffs', ['hair']),
  S('flat-top', 'Flat top', ['hair']),
  S('mohawk', 'Mohawk', ['hair']),
  S('bun', 'Bun', ['hair']),
  S('top-knot', 'Top knot', ['hair']),
  S('ponytail', 'Ponytail', ['hair']),
  S('pigtails', 'Pigtails', ['hair']),
  S('bob', 'Bob', ['hair']),
  S('long', 'Long', ['hair']),
  S('wavy', 'Wavy', ['hair']),
  S('braids', 'Braids', ['hair']),
  S('locs', 'Locs', ['hair']),
  S('mullet', 'Mullet', ['hair']),
  S('pompadour', 'Pompadour', ['hair']),
  S('bowl', 'Bowl cut', ['hair']),
  S('comb-over', 'Comb-over', ['hair']),
  S('undercut', 'Side shave', ['hair']),
  S('high-ponytail', 'High ponytail', ['hair']),
  S('twists', 'Twists', ['hair']),
  S('curtains', 'Curtains', ['hair']),
] as const satisfies readonly FacePart[];

export const FACE_EYES = [
  S('round', 'Round', ['eyes']),
  S('dots', 'Dots', ['eyes']),
  S('wide', 'Wide', ['eyes']),
  S('big', 'Big', ['eyes']),
  S('almond', 'Almond', ['eyes']),
  S('lashes', 'Lashes', ['eyes']),
  S('sleepy', 'Sleepy', ['eyes']),
  S('narrow', 'Squint', ['eyes']),
  S('happy', 'Happy', ['eyes']),
  S('wink', 'Wink', ['eyes']),
  S('stars', 'Stars', ['eyes'], { eyes: '#f2c230' }),
  S('leds', 'LEDs', ['eyes'], { eyes: '#3ee0ff' }),
  S('closed', 'Closed', ['eyes']),
  S('hearts', 'Hearts', ['eyes'], { eyes: '#e91e63' }),
  S('dizzy', 'Spirals', ['eyes']),
  S('sparkle', 'Sparkly', ['eyes']),
  S('side-eye', 'Side glance', ['eyes']),
  S('cyclops', 'One eye', ['eyes']),
] as const satisfies readonly FacePart[];

export const FACE_BROWS = [
  S('none', 'None'),
  S('thin', 'Thin', ['brows']),
  S('thick', 'Thick', ['brows']),
  S('bushy', 'Bushy', ['brows']),
  S('arched', 'Arched', ['brows']),
  S('angled', 'Stern', ['brows']),
  S('worried', 'Worried', ['brows']),
  S('unibrow', 'Unibrow', ['brows']),
  S('raised', 'One raised', ['brows']),
  S('dots', 'Dots', ['brows']),
] as const satisfies readonly FacePart[];

export const FACE_NOSES = [
  S('none', 'None'),
  S('dot', 'Dot'),
  S('button', 'Button'),
  S('round', 'Round'),
  S('pointy', 'Pointed'),
  S('long', 'Long'),
  S('wide', 'Wide'),
  S('hook', 'Curved'),
  S('clown', 'Clown', ['paint'], { paint: '#e53935' }),
  S('snout', 'Snout'),
  S('cat', 'Cat nose'),
] as const satisfies readonly FacePart[];

export const FACE_MOUTHS = [
  S('smile', 'Smile'),
  S('grin', 'Grin'),
  S('laugh', 'Laugh'),
  S('teeth', 'Teeth'),
  S('flat', 'Flat'),
  S('smirk', 'Smirk'),
  S('o', 'Oh'),
  S('tongue', 'Tongue out'),
  S('buck', 'Buck teeth'),
  S('fangs', 'Fangs'),
  S('pout', 'Pout'),
  S('grille', 'Grille'),
  seasonal('halloween', S('jagged', "Jack-o'-lantern")),
  S('braces', 'Braces'),
  S('gap', 'Gap tooth'),
  S('gum', 'Bubble gum', ['paint'], { paint: '#ff8fc4' }),
  S('whistle', 'Whistling'),
  S('mouthguard', 'Mouth guard', ['paint'], { paint: '#4fb3f6' }),
] as const satisfies readonly FacePart[];

export const FACE_FACIAL = [
  S('none', 'None'),
  S('stubble', 'Stubble', ['facial']),
  S('mustache', 'Mustache', ['facial']),
  S('handlebar', 'Handlebar', ['facial']),
  S('goatee', 'Goatee', ['facial']),
  S('soul-patch', 'Soul patch', ['facial']),
  S('chin-strap', 'Chin strap', ['facial']),
  S('full', 'Full beard', ['facial']),
  S('long', 'Long beard', ['facial']),
  S('chops', 'Sideburns', ['facial']),
  S('pencil', 'Pencil mustache', ['facial']),
  S('walrus', 'Walrus mustache', ['facial']),
] as const satisfies readonly FacePart[];

export const FACE_HEADWEAR = [
  S('none', 'None'),
  S('cap', 'Cap', ['hat', 'hatTrim']),
  S('cap-back', 'Backward cap', ['hat', 'hatTrim']),
  S('visor', 'Visor', ['hat', 'hatTrim']),
  S('beanie', 'Beanie', ['hat', 'hatTrim']),
  S('pom-beanie', 'Pom-pom beanie', ['hat', 'hatTrim']),
  S('propeller', 'Propeller cap', ['hat', 'hatTrim']),
  S('bucket', 'Bucket hat', ['hat', 'hatTrim']),
  S('cowboy', 'Cowboy hat', ['hat', 'hatTrim'], { hat: '#a8743f', hatTrim: '#5c3a1c' }),
  S('top-hat', 'Top hat', ['hat', 'hatTrim'], { hat: '#222222', hatTrim: '#c0392b' }),
  S('chef', "Chef's hat", ['hat'], { hat: '#ffffff' }),
  S('crown', 'Crown', ['hat', 'hatTrim'], { hat: '#f2c230', hatTrim: '#e0405a' }),
  earned(
    'champion',
    'Win a league’s championship.',
    S('champion-crown', 'Champion’s crown', ['hat', 'hatTrim'], {
      hat: '#f2c230',
      hatTrim: '#c62828',
    }),
  ),
  earned(
    'perfect-week',
    'Your starters score as much as any lineup your roster could have made.',
    S('trophy-hat', 'Trophy hat', ['hat', 'hatTrim'], { hat: '#f2c230', hatTrim: '#1c2a59' }),
  ),
  S('tiara', 'Tiara', ['hat', 'hatTrim'], { hat: '#d9dde3', hatTrim: '#5ec8f2' }),
  S('helmet', 'Football helmet', ['hat', 'hatTrim']),
  S('hard-hat', 'Hard hat', ['hat'], { hat: '#f5c518' }),
  S('viking', 'Viking helmet', ['hat', 'hatTrim'], { hat: '#9aa3ad', hatTrim: '#f4e9d0' }),
  S('wizard', 'Wizard hat', ['hat', 'hatTrim'], { hat: '#4b3b9a', hatTrim: '#f2c230' }),
  S('party', 'Party hat', ['hat', 'hatTrim']),
  S('pirate', 'Pirate hat', ['hat', 'hatTrim'], { hat: '#222222', hatTrim: '#f2c230' }),
  S('grad', 'Graduation cap', ['hat', 'hatTrim'], { hat: '#222222', hatTrim: '#f2c230' }),
  S('headband', 'Headband', ['hat'], { hat: '#e53935' }),
  S('bandana', 'Bandana', ['hat', 'hatTrim'], { hat: '#c62828', hatTrim: '#ffffff' }),
  S('hood', 'Hood', ['hat', 'hatTrim'], { hat: '#34436a', hatTrim: '#222b44' }),
  S('head-scarf', 'Head scarf', ['hat', 'hatTrim']),
  S('turban', 'Turban', ['hat', 'hatTrim']),
  S('headphones', 'Headphones', ['hat', 'hatTrim'], { hat: '#333333', hatTrim: '#e53935' }),
  S('headset', 'Headset', ['hat'], { hat: '#333333' }),
  S('horns', 'Horns', ['hat'], { hat: '#f4e9d0' }),
  S('antenna', 'Antenna', ['hat', 'hatTrim'], { hat: '#7d8aa0', hatTrim: '#ff5a5a' }),
  S('halo', 'Halo', ['hat'], { hat: '#f7d354' }),
  S('cat-ears', 'Cat ears', ['hat', 'hatTrim'], { hatTrim: '#f7b6c8' }),
  S('bunny-ears', 'Bunny ears', ['hat', 'hatTrim'], { hat: '#ffffff', hatTrim: '#f7b6c8' }),
  S('bow', 'Bow', ['hat'], { hat: '#e91e63' }),
  S('flowers', 'Flower crown', ['hat', 'hatTrim'], { hat: '#f06292', hatTrim: '#7cb342' }),
  S('shades-up', 'Shades up top', ['hat'], { hat: '#1d1d1d' }),
  S('sleep-mask', 'Sleep mask', ['hat', 'hatTrim'], { hat: '#7fb3ff', hatTrim: '#a9cbff' }),
  S('beret', 'Beret', ['hat'], { hat: '#a61c2e' }),
  S('fedora', 'Fedora', ['hat', 'hatTrim'], { hat: '#5c4a3a', hatTrim: '#222222' }),
  S('trucker', 'Trucker cap', ['hat', 'hatTrim']),
  S('earmuffs', 'Earmuffs', ['hat', 'hatTrim'], { hat: '#f06292', hatTrim: '#ffffff' }),
  S('space-helmet', 'Space helmet', ['hat'], { hat: '#ffffff' }),
  S('sun-hat', 'Sun hat', ['hat', 'hatTrim'], { hat: '#f4ead2', hatTrim: '#e53935' }),
  S('newsboy', 'Newsboy cap', ['hat', 'hatTrim'], { hat: '#7d8590', hatTrim: '#5a6068' }),
  S('jester', "Jester's hat", ['hat', 'hatTrim'], { hat: '#6a3db8', hatTrim: '#f2c230' }),
  S('flower', 'Flower', ['hat', 'hatTrim'], { hat: '#f06292', hatTrim: '#ffe14d' }),
  // The weekly hat's own (PLAN-2026-10-30-the-weekly-hat §3), here for anybody.
  S('traffic-cone', 'Traffic cone', ['hat', 'hatTrim'], { hat: '#ff7a1a', hatTrim: '#ffffff' }),
  S('lampshade', 'Lampshade', ['hat', 'hatTrim'], { hat: '#f4e3b1', hatTrim: '#c0392b' }),
  seasonal(
    'halloween',
    S('witch-hat', "Witch's hat", ['hat', 'hatTrim'], { hat: '#2b2340', hatTrim: '#8e44ad' }),
  ),
  seasonal(
    'halloween',
    S('pumpkin', 'Pumpkin', ['hat', 'hatTrim'], { hat: '#f28c28', hatTrim: '#3f8f4f' }),
  ),
  seasonal('halloween', S('bat-headband', 'Bat headband', ['hat'], { hat: '#2b2340' })),
  seasonal(
    'thanksgiving',
    S('turkey', 'Turkey hat', ['hat', 'hatTrim'], { hat: '#b5651d', hatTrim: '#f4ead2' }),
  ),
  seasonal(
    'winter',
    S('santa', 'Santa hat', ['hat', 'hatTrim'], { hat: '#d32f2f', hatTrim: '#ffffff' }),
  ),
  seasonal('winter', S('antlers', 'Antlers', ['hat'], { hat: '#8b5a2b' })),
  seasonal(
    'winter',
    S('elf', 'Elf hat', ['hat', 'hatTrim'], { hat: '#2e8b57', hatTrim: '#d32f2f' }),
  ),
  seasonal(
    'valentines',
    S('heart-boppers', 'Heart boppers', ['hat', 'hatTrim'], { hat: '#e91e63', hatTrim: '#333333' }),
  ),
  seasonal(
    'st-patricks',
    S('shamrock-hat', 'Shamrock hat', ['hat', 'hatTrim'], { hat: '#2e8b57', hatTrim: '#f2c230' }),
  ),
] as const satisfies readonly FacePart[];

export const FACE_EYEWEAR = [
  S('none', 'None'),
  S('round', 'Round glasses', ['frames', 'lenses'], { lenses: '#cfe8ff' }),
  S('square', 'Square glasses', ['frames', 'lenses'], { lenses: '#cfe8ff' }),
  S('cat-eye', 'Cat-eye glasses', ['frames', 'lenses'], { lenses: '#cfe8ff' }),
  S('big', 'Big glasses', ['frames', 'lenses'], { lenses: '#cfe8ff' }),
  S('readers', 'Reading glasses', ['frames'], { frames: '#8a5a2b' }),
  S('shades', 'Sunglasses', ['frames', 'lenses'], { frames: '#1d1d1d', lenses: '#22252b' }),
  S('sport', 'Sport shades', ['frames', 'lenses'], { frames: '#e53935', lenses: '#2a3a5a' }),
  S('goggles', 'Goggles', ['frames', 'lenses'], { frames: '#ff7a00', lenses: '#7fd3ff' }),
  S('3d', '3D glasses', ['frames'], { frames: '#f4f4f4' }),
  S('monocle', 'Monocle', ['frames'], { frames: '#c9a227' }),
  S('patch', 'Eye patch', ['frames'], { frames: '#1d1d1d' }),
  S('hearts', 'Heart shades', ['frames', 'lenses'], { frames: '#e91e63', lenses: '#ff8fb3' }),
  S('stars', 'Star shades', ['frames', 'lenses'], { frames: '#f2c230', lenses: '#ffe27a' }),
  S('aviators', 'Aviators', ['frames', 'lenses'], { frames: '#c9a227', lenses: '#3a4a5a' }),
  S('snorkel', 'Snorkel mask', ['frames', 'lenses'], { frames: '#ff7a00', lenses: '#bfe9ff' }),
] as const satisfies readonly FacePart[];

export const FACE_NECK = [
  S('none', 'None'),
  S('tie', 'Tie', ['neck', 'neckTrim'], { neck: '#7a1f2b', neckTrim: '#f4f4f4' }),
  S('bowtie', 'Bow tie', ['neck'], { neck: '#c0392b' }),
  // THE GARMENTS ARE OUTFITS NOW (§2.1), kept here for the faces that wear one.
  retired(S('collar', 'Shirt collar', ['neck'], { neck: '#ffffff' })),
  retired(S('jersey', 'Jersey', ['neck', 'neckTrim'])),
  retired(S('hoodie', 'Hoodie', ['neck', 'neckTrim'])),
  retired(S('turtleneck', 'Turtleneck', ['neck'])),
  S('scarf', 'Scarf', ['neck', 'neckTrim']),
  S('neckerchief', 'Neckerchief', ['neck'], { neck: '#e53935' }),
  S('pearls', 'Pearls', ['neck'], { neck: '#fbf7ee' }),
  S('medal', 'Gold medal', ['neck', 'neckTrim'], { neck: '#e53935', neckTrim: '#f2c230' }),
  S('lanyard', 'Lanyard', ['neck', 'neckTrim'], { neck: '#1e88e5', neckTrim: '#ffffff' }),
  S('whistle', 'Whistle', ['neck', 'neckTrim'], { neck: '#d32f2f', neckTrim: '#cfd4da' }),
  S('chain', 'Gold chain', ['neck'], { neck: '#f2c230' }),
  retired(
    S('overalls', 'Overalls', ['neck', 'neckTrim'], { neck: '#3c78c8', neckTrim: '#e53935' }),
  ),
  seasonal(
    'halloween',
    S('cape', 'Vampire cape', ['neck', 'neckTrim'], { neck: '#1d1d1d', neckTrim: '#a61c2e' }),
  ),
  retired(
    seasonal(
      'winter',
      S('sweater', 'Holiday sweater', ['neck', 'neckTrim'], {
        neck: '#c62828',
        neckTrim: '#ffffff',
      }),
    ),
  ),
] as const satisfies readonly FacePart[];

export const FACE_MARKS = [
  S('freckles', 'Freckles'),
  S('blush', 'Blush'),
  S('dimples', 'Dimples'),
  S('mole', 'Beauty mark'),
  S('eye-black', 'Eye black'),
  S('stripes', 'Cheek stripes', ['paint']),
  S('star', 'Star sticker', ['paint']),
  S('heart', 'Heart sticker', ['paint']),
  S('bandage', 'Bandage'),
  S('sweat', 'Sweat drop'),
  S('earrings', 'Earrings'),
  S('whiskers', 'Whiskers'),
  S('sparkles', 'Sparkles'),
  S('bolt', 'Lightning bolt', ['paint'], { paint: '#f2c230' }),
  S('daisy', 'Daisy', ['paint'], { paint: '#ffffff' }),
  seasonal('halloween', S('web', 'Spider web', ['paint'], { paint: '#2b2340' })),
  seasonal('winter', S('snowflake', 'Snowflake', ['paint'], { paint: '#7fd3ff' })),
  seasonal('st-patricks', S('shamrock', 'Shamrock', ['paint'], { paint: '#2e8b57' })),
] as const satisfies readonly FacePart[];

/** How many extras one face wears at once. */
export const FACE_MARKS_MAX = 4;

/** What the character's outfit is painted in: itself, its trim and number, its pants, its shoes. */
const DRESSED: readonly FaceArea[] = ['shirt', 'shirtTrim', 'pants', 'shoes'];
/** An outfit that is one piece, its legs its own color: no pants of their own. */
const ONE_PIECE: readonly FaceArea[] = ['shirt', 'shirtTrim', 'shoes'];

/**
 * The character's outfit (§2.1), which a badge shows the shoulders of once it
 * is chosen. The first six were worn at the neck before there was a body, and
 * keep their ids: a face that wears one there wears it as its outfit
 * (`faceOutfit`). An outfit with no colors of its own is worn in the team's.
 */
export const FACE_OUTFITS = [
  S('jersey', 'Jersey', DRESSED),
  S('tee', 'T-shirt', DRESSED),
  S('hoodie', 'Hoodie', DRESSED),
  S('collar', 'Button-up shirt', DRESSED, { shirt: '#ffffff', pants: '#3b4a63' }),
  S('turtleneck', 'Turtleneck', DRESSED),
  S('overalls', 'Overalls', ONE_PIECE, { shirt: '#3c78c8', shirtTrim: '#e53935' }),
  S('suit', 'Suit', DRESSED, { shirt: '#2b3245', shirtTrim: '#ffffff', pants: '#2b3245' }),
  S('referee', 'Referee stripes', DRESSED, {
    shirt: '#ffffff',
    shirtTrim: '#111111',
    pants: '#111111',
  }),
  S('coach', "Coach's jacket", DRESSED),
  S('puffer', 'Puffer jacket', DRESSED),
  S('tracksuit', 'Tracksuit', ONE_PIECE),
  S('tank', 'Tank top', DRESSED),
  S('dress', 'Sundress', ONE_PIECE),
  S('super', 'Super suit', ONE_PIECE),
  S('robot', 'Robot body', ONE_PIECE, { shirt: '#b9c6d8', shirtTrim: '#3ee0ff' }),
  seasonal(
    'winter',
    S('sweater', 'Holiday sweater', DRESSED, { shirt: '#c62828', shirtTrim: '#ffffff' }),
  ),
  seasonal(
    'winter',
    S('santa', 'Santa suit', ONE_PIECE, { shirt: '#d32f2f', shirtTrim: '#ffffff' }),
  ),
  seasonal(
    'halloween',
    S('pumpkin-suit', 'Pumpkin suit', ONE_PIECE, { shirt: '#ff7a00', shirtTrim: '#2e7d32' }),
  ),
  seasonal(
    'thanksgiving',
    S('flannel', 'Flannel shirt', DRESSED, {
      shirt: '#b23a2b',
      shirtTrim: '#2b2b2b',
      pants: '#3c5a8a',
    }),
  ),
  seasonal(
    'valentines',
    S('heart-tee', 'Heart T-shirt', DRESSED, { shirt: '#ffffff', shirtTrim: '#e53950' }),
  ),
  seasonal(
    'st-patricks',
    S('leprechaun', "Leprechaun's vest", DRESSED, { shirt: '#2e8b57', shirtTrim: '#f2c230' }),
  ),
] as const satisfies readonly FacePart[];

/** The outfits that carry the number on their front. */
export const NUMBERED_OUTFITS: ReadonlySet<string> = new Set(['jersey', 'tee', 'tank']);

/**
 * What the character holds (§2.1): in a hand, or a parrot on its shoulder.
 * Drawn on the character, not on a badge, which is a face.
 */
export const FACE_HELD = [
  S('none', 'Nothing'),
  S('football', 'Football', ['held'], { held: '#8b4a1c' }),
  S('foam-finger', 'Foam finger', ['held']),
  S('pennant', 'Pennant', ['held']),
  S('pom-poms', 'Pom-poms', ['held']),
  S('megaphone', 'Megaphone', ['held']),
  S('balloon', 'Balloon', ['held'], { held: '#e53935' }),
  S('popcorn', 'Popcorn', ['held'], { held: '#e53935' }),
  S('soda', 'Soda', ['held'], { held: '#1e66d0' }),
  S('clipboard', 'Clipboard', ['held'], { held: '#a0522d' }),
  S('teddy', 'Teddy bear', ['held'], { held: '#a86b45' }),
  S('puppy', 'Puppy', ['held'], { held: '#d9a066' }),
  S('flowers', 'Flowers', ['held'], { held: '#f06292' }),
  S('mug', 'Mug', ['held'], { held: '#ffffff' }),
  S('parrot', 'Parrot', ['held'], { held: '#2e8b57' }),
  earned(
    'champion',
    'Win a league’s championship.',
    S('trophy', 'Champion’s trophy', ['held'], { held: '#f2c230' }),
  ),
  earned('first-w', 'Win a matchup.', S('game-ball', 'Game ball', ['held'], { held: '#8b4a1c' })),
  seasonal('halloween', S('candy-pail', 'Pumpkin pail', ['held'], { held: '#ff7a00' })),
  seasonal('thanksgiving', S('pie', 'Pie', ['held'], { held: '#d98c5f' })),
  seasonal('winter', S('present', 'Present', ['held'], { held: '#c62828' })),
  seasonal('valentines', S('heart-balloon', 'Heart balloon', ['held'], { held: '#e53950' })),
  seasonal('st-patricks', S('pot-of-gold', 'Pot of gold', ['held'], { held: '#2b2b2b' })),
] as const satisfies readonly FacePart[];

type IdOf<T extends readonly { id: string }[]> = T[number]['id'];
export type FaceHead = IdOf<typeof FACE_HEADS>;
export type FaceEars = IdOf<typeof FACE_EARS>;
export type FaceHair = IdOf<typeof FACE_HAIR>;
export type FaceEyes = IdOf<typeof FACE_EYES>;
export type FaceBrows = IdOf<typeof FACE_BROWS>;
export type FaceNose = IdOf<typeof FACE_NOSES>;
export type FaceMouth = IdOf<typeof FACE_MOUTHS>;
export type FaceFacial = IdOf<typeof FACE_FACIAL>;
export type FaceHeadwear = IdOf<typeof FACE_HEADWEAR>;
export type FaceEyewear = IdOf<typeof FACE_EYEWEAR>;
export type FaceNeck = IdOf<typeof FACE_NECK>;
export type FaceMark = IdOf<typeof FACE_MARKS>;
export type FaceOutfit = IdOf<typeof FACE_OUTFITS>;
export type FaceHeld = IdOf<typeof FACE_HELD>;

const idsOf = <T extends readonly { id: string }[]>(list: T) =>
  z.enum(list.map((p) => p.id) as [IdOf<T>, ...IdOf<T>[]]);

// ── The categories, in the maker's order ───────────────────────────────────

/** A face's single-choice parts, each a category of the maker. */
export type FacePartKey =
  | 'head'
  | 'ears'
  | 'hair'
  | 'eyes'
  | 'brows'
  | 'nose'
  | 'mouth'
  | 'facial'
  | 'headwear'
  | 'eyewear'
  | 'neck'
  | 'body'
  | 'held';
export type FaceCategory = FacePartKey | 'marks';

/** Every category with its name and its choices, in the order the maker shows them. */
export const FACE_CATEGORIES: readonly {
  key: FaceCategory;
  name: string;
  parts: readonly FacePart[];
}[] = [
  { key: 'head', name: 'Head', parts: FACE_HEADS },
  { key: 'ears', name: 'Ears', parts: FACE_EARS },
  { key: 'hair', name: 'Hair', parts: FACE_HAIR },
  { key: 'eyes', name: 'Eyes', parts: FACE_EYES },
  { key: 'brows', name: 'Brows', parts: FACE_BROWS },
  { key: 'nose', name: 'Nose', parts: FACE_NOSES },
  { key: 'mouth', name: 'Mouth', parts: FACE_MOUTHS },
  { key: 'facial', name: 'Beard', parts: FACE_FACIAL },
  { key: 'headwear', name: 'Hat', parts: FACE_HEADWEAR },
  { key: 'eyewear', name: 'Glasses', parts: FACE_EYEWEAR },
  { key: 'neck', name: 'Neck', parts: FACE_NECK },
  { key: 'marks', name: 'Extras', parts: FACE_MARKS },
  { key: 'body', name: 'Outfit', parts: FACE_OUTFITS },
  { key: 'held', name: 'Hold', parts: FACE_HELD },
];

/** The categories the character has and a badge does not show: the maker draws the character for them. */
export const FIGURE_CATEGORIES: ReadonlySet<FaceCategory> = new Set(['body', 'held']);

/** A category's choice by id, or undefined for one the catalog does not have. */
export function facePart(category: FaceCategory, id: string): FacePart | undefined {
  return FACE_CATEGORIES.find((c) => c.key === category)?.parts.find((p) => p.id === id);
}

/** Every part a face wears, category by category, extras included. */
function wornParts(face: Face): FacePart[] {
  const out: FacePart[] = [];
  for (const c of FACE_CATEGORIES) {
    const ids: readonly (string | undefined)[] =
      c.key === 'marks' ? face.marks : [face[c.key as FacePartKey]];
    for (const id of ids) {
      const part = id === undefined ? undefined : facePart(c.key, id);
      if (part) out.push(part);
    }
  }
  return out;
}

/**
 * Every achievement that earns a part of a team's look, by id: a face's part
 * or a banner (`TEAM_BANNERS`). What a store asks an account about.
 */
export function faceEarners(): string[] {
  const ids = new Set<string>();
  for (const c of FACE_CATEGORIES) for (const p of c.parts) if (p.earnedBy) ids.add(p.earnedBy);
  for (const b of TEAM_BANNERS as readonly BannerChoice[]) if (b.earnedBy) ids.add(b.earnedBy);
  return [...ids].sort();
}

/**
 * A face with the earned parts its wearer may not wear taken off (§5): each
 * part earned by an achievement not in `earned`, unless `kept` (the face it
 * replaces) already wears it. What a file brings in, a seat changing hands
 * and a face started from somebody else's are read through this, so nobody
 * comes to wear a crown they never won.
 */
export function withoutUnearned(
  face: Face,
  earned: readonly string[] = [],
  kept: Face | null = null,
): Face {
  const had = new Set(kept ? wornParts(kept) : []);
  const allowed = (p: FacePart | undefined) =>
    p?.earnedBy === undefined || earned.includes(p.earnedBy) || had.has(p);
  let out = face;
  for (const c of FACE_CATEGORIES) {
    if (c.key === 'marks') {
      const marks = out.marks.filter((m) => allowed(facePart('marks', m)));
      if (marks.length !== out.marks.length) out = { ...out, marks };
      continue;
    }
    const id = out[c.key as FacePartKey];
    if (id === undefined || allowed(facePart(c.key, id))) continue;
    if (c.key === 'body') {
      const { body: _b, ...rest } = out;
      out = rest;
    } else out = chooseFacePart(out, c.key, 'none');
  }
  return out;
}

/**
 * The achievements a face needs its wearer to have earned, for the parts it
 * wears that are earned (§5); none for a face that wears none of them. Parts
 * `kept` wears already are not counted: a face is never refused for keeping
 * what it had.
 */
export function faceNeeds(face: Face, kept: Face | null = null): string[] {
  const had = new Set(kept ? wornParts(kept) : []);
  const ids = new Set<string>();
  for (const p of wornParts(face)) if (p.earnedBy && !had.has(p)) ids.add(p.earnedBy);
  return [...ids].sort();
}

// ── Adjustment ─────────────────────────────────────────────────────────────

/**
 * Whole steps a part moves by, each within its range (Mii's steppers): a
 * step is a thing a second client can draw the same way; a slider's
 * position is not.
 */
export const FACE_ADJUST_RANGES = {
  eyesY: [-3, 3],
  eyesGap: [-3, 3],
  eyesSize: [-2, 2],
  browsY: [-3, 3],
  noseY: [-2, 2],
  noseSize: [-2, 2],
  mouthY: [-2, 3],
  mouthSize: [-2, 2],
} as const;
export type FaceAdjustKey = keyof typeof FACE_ADJUST_RANGES;

const step = (key: FaceAdjustKey) =>
  z.number().int().min(FACE_ADJUST_RANGES[key][0]).max(FACE_ADJUST_RANGES[key][1]).default(0);

export const FaceAdjust = z.object({
  eyesY: step('eyesY'),
  eyesGap: step('eyesGap'),
  eyesSize: step('eyesSize'),
  browsY: step('browsY'),
  noseY: step('noseY'),
  noseSize: step('noseSize'),
  mouthY: step('mouthY'),
  mouthSize: step('mouthSize'),
});
export type FaceAdjust = z.infer<typeof FaceAdjust>;

export const NO_ADJUST: FaceAdjust = {
  eyesY: 0,
  eyesGap: 0,
  eyesSize: 0,
  browsY: 0,
  noseY: 0,
  noseSize: 0,
  mouthY: 0,
  mouthSize: 0,
};

// ── The face ───────────────────────────────────────────────────────────────

export const FaceColors = z.object({
  skin: Hex,
  hair: Hex,
  brows: Hex,
  facial: Hex,
  eyes: Hex,
  hat: Hex,
  hatTrim: Hex,
  frames: Hex,
  lenses: Hex,
  neck: Hex,
  neckTrim: Hex,
  paint: Hex,
  // THE CHARACTER'S AREAS, optional (§2.1): absent is the color
  // `figureColors` resolves, the team's for the outfit. Added 2026-10-24.
  shirt: Hex.optional(),
  shirtTrim: Hex.optional(),
  pants: Hex.optional(),
  shoes: Hex.optional(),
  held: Hex.optional(),
}) satisfies z.ZodType<Record<FaceSlot, string> & Partial<Record<FigureSlot, string>>>;
export type FaceColors = z.infer<typeof FaceColors>;

/**
 * A face, on the wire and as stored inside a team's badge (`teams.badge_json`,
 * kind `face`). Every part one the catalog has, every color six hex digits,
 * every step in range and no more than four extras, or it is refused.
 */
export const Face = z.object({
  head: idsOf(FACE_HEADS),
  ears: idsOf(FACE_EARS),
  hair: idsOf(FACE_HAIR),
  eyes: idsOf(FACE_EYES),
  brows: idsOf(FACE_BROWS),
  nose: idsOf(FACE_NOSES),
  mouth: idsOf(FACE_MOUTHS),
  facial: idsOf(FACE_FACIAL),
  headwear: idsOf(FACE_HEADWEAR),
  eyewear: idsOf(FACE_EYEWEAR),
  neck: idsOf(FACE_NECK),
  /**
   * The character's outfit (§2.1). Absent, it wears a garment it wears at
   * the neck, or else the team's jersey, and its badge shows the face alone.
   * Added 2026-10-24.
   */
  body: idsOf(FACE_OUTFITS).optional(),
  /** The number on its jersey: one or two digits, as football's are, `0` and `00` included. */
  number: z
    .string()
    .regex(/^\d{1,2}$/, 'A number is one or two digits.')
    .optional(),
  /** What the character holds; absent, nothing. */
  held: idsOf(FACE_HELD).optional(),
  // A BACKDROP is the team's banner now (PLAN-2026-10-24-team-banners), not a
  // face's: a face stored with one parses without it, as any key the schema
  // does not know is left out, and migration 0085 took it off the stored ones.
  /** Its extras, each at most once. */
  marks: z
    .array(idsOf(FACE_MARKS))
    .max(FACE_MARKS_MAX)
    .refine((m) => new Set(m).size === m.length, 'An extra is worn once.')
    .default([]),
  colors: FaceColors,
  adjust: FaceAdjust.default(NO_ADJUST),
});
export type Face = z.infer<typeof Face>;

/**
 * What a face is doing (AI_MANAGERS.md §5.1): at rest, on the clock, a good
 * week, a bad one, speaking; for the stickers (PLAN-2026-10-30-stickers §2),
 * taken aback, asleep and waving hello; and where things happen
 * (PLAN-2026-10-31-a-handshake-a-dance-and-the-photo), shaking on a trade and
 * dancing over a claim won.
 */
export type FaceMood =
  | 'idle'
  | 'thinking'
  | 'pleased'
  | 'annoyed'
  | 'talking'
  | 'surprised'
  | 'asleep'
  | 'waving'
  | 'shaking'
  | 'dancing';
export const FACE_MOODS: readonly { id: FaceMood; name: string }[] = [
  { id: 'idle', name: 'At rest' },
  { id: 'pleased', name: 'Pleased' },
  { id: 'annoyed', name: 'Annoyed' },
  { id: 'thinking', name: 'Thinking' },
  { id: 'talking', name: 'Talking' },
  { id: 'surprised', name: 'Surprised' },
  { id: 'asleep', name: 'Asleep' },
  { id: 'waving', name: 'Waving' },
  { id: 'shaking', name: 'Shaking hands' },
  { id: 'dancing', name: 'Dancing' },
];

// ── The palettes ───────────────────────────────────────────────────────────

/** A color with its name, for a swatch's tooltip and a screen reader. */
export interface NamedColor {
  readonly name: string;
  readonly hex: string;
}

/** Skin: people's range first, then the fantasy ones a gremlin or a robot wears. */
export const SKIN_TONES: readonly NamedColor[] = [
  { name: 'Porcelain', hex: '#fbe3d0' },
  { name: 'Ivory', hex: '#f5d3b8' },
  { name: 'Peach', hex: '#ffdcb0' },
  { name: 'Blush', hex: '#f3c9a8' },
  { name: 'Apricot', hex: '#e8b48a' },
  { name: 'Sand', hex: '#f1c27d' },
  { name: 'Honey', hex: '#e0ac69' },
  { name: 'Golden', hex: '#d9a066' },
  { name: 'Caramel', hex: '#c68642' },
  { name: 'Bronze', hex: '#a86b45' },
  { name: 'Chestnut', hex: '#8d5524' },
  { name: 'Cocoa', hex: '#6b3e26' },
  { name: 'Espresso', hex: '#4a2c1d' },
  { name: 'Gremlin green', hex: '#7cc350' },
  { name: 'Sky blue', hex: '#8fc8f0' },
  { name: 'Lavender', hex: '#b9a4e8' },
  { name: 'Bubblegum', hex: '#f7a8c8' },
  { name: 'Sunny yellow', hex: '#ffd84d' },
  { name: 'Robot silver', hex: '#b9c6d8' },
];
/** How many of `SKIN_TONES` are people's, before the fantasy ones. */
export const SKIN_TONES_HUMAN = 13;

/** Hair, brows and beards: the natural range first, then the fun ones. */
export const HAIR_COLORS: readonly NamedColor[] = [
  { name: 'Jet black', hex: '#1b1b1b' },
  { name: 'Black', hex: '#262626' },
  { name: 'Espresso', hex: '#3a1d0e' },
  { name: 'Dark brown', hex: '#5a3a1a' },
  { name: 'Brown', hex: '#6b4423' },
  { name: 'Auburn', hex: '#8b3a1e' },
  { name: 'Copper', hex: '#a0522d' },
  { name: 'Ginger', hex: '#c8642b' },
  { name: 'Strawberry', hex: '#d98c5f' },
  { name: 'Honey blond', hex: '#e0b03a' },
  { name: 'Golden', hex: '#ffcf33' },
  { name: 'Platinum', hex: '#f0e6c8' },
  { name: 'Silver', hex: '#d6d6d6' },
  { name: 'Gray', hex: '#9e9e9e' },
  { name: 'Electric blue', hex: '#2f7cf6' },
  { name: 'Hot pink', hex: '#ff4fa3' },
  { name: 'Purple', hex: '#8e44ad' },
  { name: 'Mint', hex: '#4fd1a5' },
  { name: 'Moss', hex: '#3f6d22' },
];
export const HAIR_COLORS_NATURAL = 14;

export const EYE_COLORS: readonly NamedColor[] = [
  { name: 'Dark', hex: '#2b1d16' },
  { name: 'Brown', hex: '#5b3a1e' },
  { name: 'Hazel', hex: '#8a6d2f' },
  { name: 'Green', hex: '#3f8f4f' },
  { name: 'Blue', hex: '#3c78c8' },
  { name: 'Gray', hex: '#6f7d8c' },
  { name: 'Amber', hex: '#c88a1e' },
  { name: 'Violet', hex: '#7b4fc8' },
  { name: 'Gold', hex: '#f2c230' },
  { name: 'Cyan glow', hex: '#3ee0ff' },
  { name: 'Red glow', hex: '#ff4d4d' },
];

/** Everything else: hats, glasses, what is at the neck, face paint. */
export const PAINT_COLORS: readonly NamedColor[] = [
  { name: 'White', hex: '#ffffff' },
  { name: 'Cream', hex: '#f4ead2' },
  { name: 'Silver', hex: '#c9ced6' },
  { name: 'Gray', hex: '#7d8590' },
  { name: 'Charcoal', hex: '#333333' },
  { name: 'Black', hex: '#111111' },
  { name: 'Red', hex: '#e53935' },
  { name: 'Crimson', hex: '#a61c2e' },
  { name: 'Orange', hex: '#ff7a00' },
  { name: 'Gold', hex: '#f2c230' },
  { name: 'Yellow', hex: '#ffe14d' },
  { name: 'Lime', hex: '#9ccc3c' },
  { name: 'Green', hex: '#2e8b57' },
  { name: 'Forest', hex: '#1f5a3a' },
  { name: 'Teal', hex: '#138d8a' },
  { name: 'Sky', hex: '#4fb3f6' },
  { name: 'Blue', hex: '#1e66d0' },
  { name: 'Navy', hex: '#1c2a59' },
  { name: 'Purple', hex: '#6a3db8' },
  { name: 'Violet', hex: '#a77be0' },
  { name: 'Pink', hex: '#f06292' },
  { name: 'Magenta', hex: '#c2185b' },
  { name: 'Brown', hex: '#7a4a26' },
  { name: 'Tan', hex: '#d2b48c' },
];

/** Which palette an area's swatches come from (the team's two colors join the paints in the maker). */
export function paletteFor(slot: FaceArea): readonly NamedColor[] {
  if (slot === 'skin') return SKIN_TONES;
  if (slot === 'hair' || slot === 'brows' || slot === 'facial') return HAIR_COLORS;
  if (slot === 'eyes') return EYE_COLORS;
  return PAINT_COLORS;
}

export const DEFAULT_FACE_COLORS: FaceColors = {
  skin: '#f1c27d',
  hair: '#6b4423',
  brows: '#6b4423',
  facial: '#6b4423',
  eyes: '#2b1d16',
  hat: '#1e66d0',
  hatTrim: '#ffffff',
  frames: '#333333',
  lenses: '#cfe8ff',
  neck: '#e53935',
  neckTrim: '#ffffff',
  paint: '#e53935',
};

/** A color a little darker, for the shading on itself (a nose on its skin, a brow on its hair). */
export function darken(hex: string, by = 0.72): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * by);
  const g = Math.round(((n >> 8) & 255) * by);
  const b = Math.round((n & 255) * by);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// ── Changing a face ────────────────────────────────────────────────────────

/**
 * A face with one category's choice changed, bringing that choice's own
 * colors (a crown is gold; a cap keeps the hat the colors it was). For
 * extras, the choice is worn or taken off, and a fifth is not added.
 */
export function chooseFacePart(face: Face, category: FaceCategory, id: string): Face {
  const part = facePart(category, id);
  if (part === undefined) return face;
  const colors = { ...face.colors, ...part.paints };
  if (category === 'marks') {
    const mark = id as FaceMark;
    const on = face.marks.includes(mark);
    if (!on && face.marks.length >= FACE_MARKS_MAX) return face;
    return {
      ...face,
      colors,
      marks: on ? face.marks.filter((m) => m !== mark) : [...face.marks, mark],
    };
  }
  if (category === 'held') {
    // A THING HELD BRINGS ITS OWN COLOR, or the team's: the last thing's paint
    // is not left on it. NOTHING HELD IS NO KEY, so a face that never held
    // anything and one that put it down are the same face.
    const { held: _c, ...colors } = face.colors;
    const { held: _h, ...rest } = face;
    return id === 'none'
      ? { ...rest, colors }
      : { ...rest, colors: { ...colors, ...part.paints }, held: id as FaceHeld };
  }
  if (category === 'body') {
    // AN OUTFIT BRINGS ITS OWN COLORS, and one with none of its own is worn in
    // the team's, so a jersey chosen after a referee's stripes is the team's
    // jersey again. It replaces a garment worn at the neck (§2.1).
    const { shirt: _s, shirtTrim: _t, pants: _p, ...rest } = face.colors;
    const neck = GARMENT_NECKS.has(face.neck) ? 'none' : face.neck;
    return { ...face, neck, colors: { ...rest, ...part.paints }, body: id as FaceOutfit };
  }
  return { ...face, colors, [category]: id } as Face;
}

/**
 * A face with an area repainted. Hair carries the brows and the beard with it
 * while they match it, as Bitmoji's does, unless `alone` (the paint can,
 * which paints only what it is tapped on).
 */
export function paintFace(face: Face, slot: FaceArea, hex: string, alone = false): Face {
  const colors = { ...face.colors, [slot]: hex };
  if (slot === 'hair' && !alone) {
    if (face.colors.brows === face.colors.hair) colors.brows = hex;
    if (face.colors.facial === face.colors.hair) colors.facial = hex;
  }
  return { ...face, colors };
}

/** A face with one adjustment stepped, held to its range. */
export function stepFace(face: Face, key: FaceAdjustKey, by: number): Face {
  const [lo, hi] = FACE_ADJUST_RANGES[key];
  const next = Math.max(lo, Math.min(hi, face.adjust[key] + by));
  return { ...face, adjust: { ...face.adjust, [key]: next } };
}

/**
 * The areas a face is painted in now: skin and eyes always, the rest as its
 * parts have them. The character's outfit is counted as `faceOutfit` reads
 * it, so a face that has chosen none still has the team jersey's areas.
 */
export function faceSlotsInUse(face: Face): FaceArea[] {
  const used = new Set<FaceArea>(['skin', 'eyes']);
  for (const p of wornParts(face)) for (const s of p.slots) used.add(s);
  const outfit = faceOutfit(face);
  if (outfit.from !== 'neck') for (const s of facePart('body', outfit.id)?.slots ?? []) used.add(s);
  return FACE_AREAS.filter((s) => used.has(s));
}

// ── The character ──────────────────────────────────────────────────────────

/**
 * What was worn at the neck before there was a body, and is an outfit now
 * (§2.1): the Neck grid no longer offers them, and a face wearing one there
 * wears it as its outfit.
 */
export const GARMENT_NECKS: ReadonlySet<string> = new Set(
  FACE_NECK.filter((p) => p.retired).map((p) => p.id),
);

/** What a face's character wears, and where its colors come from. */
export interface WornOutfit {
  readonly id: FaceOutfit;
  /**
   * `body` for an outfit chosen; `neck` for a garment worn at the neck before
   * there was a body, painted in the neck's areas; `team` for the team's
   * jersey a face that has chosen nothing wears.
   */
  readonly from: 'body' | 'neck' | 'team';
}

/**
 * The outfit a face's character wears (§2.1). A badge shows its shoulders
 * unless it is the team's. A robot's head that has chosen none stands on a
 * robot's body, in its own metal.
 */
export function faceOutfit(face: Face): WornOutfit {
  if (face.body !== undefined) return { id: face.body, from: 'body' };
  if (GARMENT_NECKS.has(face.neck)) return { id: face.neck as FaceOutfit, from: 'neck' };
  return { id: face.head === 'robot' ? 'robot' : 'jersey', from: 'team' };
}

/**
 * A face whose outfit is worn at its neck, with it moved to the body: the
 * same garment in the same colors, the neck left bare. The maker does this
 * as it opens a face, so its Outfit tab is where the garment is.
 */
export function wearOutfit(face: Face): Face {
  const outfit = faceOutfit(face);
  if (outfit.from !== 'neck') return face;
  return {
    ...face,
    body: outfit.id,
    neck: 'none',
    colors: { ...face.colors, shirt: face.colors.neck, shirtTrim: face.colors.neckTrim },
  };
}

/** A team's two colors, as a face's character wears them. */
export interface FigureTeam {
  readonly primary: string;
  readonly secondary: string;
}

/** The colors a character with no team is drawn in: the gallery's, a picture with no team. */
export const NO_TEAM: FigureTeam = { primary: '#1e66d0', secondary: '#ffffff' };

/**
 * Every area's color, the character's resolved (§2.1): a painted area keeps
 * its paint; an outfit nobody painted is the team's second color with its
 * main color for the trim and the number, so it stands out on the team's
 * disc, and a robot's own body is its metal with its eyes' glow; pants are
 * white and shoes near-black; and a held thing is the team's main color, so
 * it stands out on the jersey.
 */
export function figureColors(face: Face, team: FigureTeam = NO_TEAM): Record<FaceArea, string> {
  const c = face.colors;
  const outfit = faceOutfit(face);
  const neck = outfit.from === 'neck';
  const metal = outfit.from === 'team' && outfit.id === 'robot';
  return {
    ...c,
    shirt: neck ? c.neck : metal ? (c.shirt ?? c.skin) : (c.shirt ?? team.secondary),
    shirtTrim: neck ? c.neckTrim : metal ? (c.shirtTrim ?? c.eyes) : (c.shirtTrim ?? team.primary),
    pants: c.pants ?? '#eeeeee',
    shoes: c.shoes ?? '#262626',
    held: c.held ?? team.primary,
  };
}

// ── Shuffling ──────────────────────────────────────────────────────────────

/** Random numbers in [0, 1), passed in so a test can hold a shuffle to a seed. */
export type Rng = () => number;

/** A small seeded generator (mulberry32): the same seed, the same faces. */
export function seededRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rng: Rng, list: readonly T[]): T {
  return list[Math.floor(rng() * list.length) % list.length]!;
}

/** One of `weights`' keys, each as likely as its weight; a key missing from it never. */
function weighted<T extends string>(rng: Rng, weights: Partial<Record<T, number>>): T {
  const entries = (Object.entries(weights) as [T, number | undefined][]).filter(
    (e): e is [T, number] => (e[1] ?? 0) > 0,
  );
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = rng() * total;
  for (const [k, w] of entries) {
    r -= w;
    if (r < 0) return k;
  }
  return entries[entries.length - 1]![0];
}

/** How likely each choice of a category is, by id. */
type Weights = Partial<Record<string, number>>;

/** Every id of a list at the same weight, `none` at its own. */
function evenly<T extends string>(
  ids: readonly T[],
  none: number,
  each = 1,
): Partial<Record<T, number>> {
  const w: Partial<Record<T, number>> = {};
  for (const id of ids) w[id] = id === 'none' ? none : each;
  return w;
}

const ids = <T extends readonly { id: string }[]>(list: T) => list.map((p) => p.id) as IdOf<T>[];

/** What a robot's head picks for itself, whatever the theme: a robot's ears, eyes, mouth and the rest. */
const ROBOT_PICKS: ReadonlySet<FacePartKey> = new Set([
  'ears',
  'hair',
  'eyes',
  'brows',
  'nose',
  'mouth',
  'facial',
]);

/**
 * A category picked the way a person would, with a season's parts now and
 * then while it is on. A theme's weights, where it has them for the category,
 * come first, and say for themselves which seasonal parts they wear; but a
 * robot's head keeps a robot's face, so a pirate robot wears the pirate's hat
 * and not the pirate's brows.
 */
function randomPart(
  rng: Rng,
  category: FacePartKey,
  face: Pick<Face, 'head'>,
  theme: FaceTheme = ANYTHING,
  on?: Date,
): string {
  const themed = theme.parts?.[category];
  if (themed && !(face.head === 'robot' && ROBOT_PICKS.has(category)))
    return weighted<string>(rng, chosenOnly(category, themed));
  return weighted<string>(
    rng,
    chosenOnly(category, inSeason(category, everyday(category, face), on)),
  );
}

/**
 * Weights without the parts a shuffle never picks: a retired one, and one
 * earned by playing, which its earner chooses for themselves (§5).
 */
function chosenOnly(category: FacePartKey, weights: Weights): Weights {
  const out: Weights = {};
  for (const [id, w] of Object.entries(weights)) {
    const part = facePart(category, id);
    if (part && !part.retired && part.earnedBy === undefined) out[id] = w;
  }
  return out;
}

/** A category's weights the way a person would pick: most faces have no beard and no glasses. */
function everyday(category: FacePartKey, face: Pick<Face, 'head'>): Weights {
  const robot = face.head === 'robot';
  switch (category) {
    case 'head':
      return {
        round: 30,
        oval: 20,
        square: 14,
        long: 10,
        heart: 10,
        wide: 12,
        robot: 4,
      };
    case 'ears':
      return robot
        ? { bolts: 7, none: 3 }
        : { round: 50, small: 20, big: 12, pointy: 8, floppy: 4, none: 4 };
    case 'hair':
      return robot ? { none: 8, spiky: 1, mohawk: 1 } : evenly(ids(FACE_HAIR), 3, 4);
    case 'eyes':
      return robot
        ? { leds: 7, round: 1, big: 1, stars: 1 }
        : {
            round: 18,
            dots: 10,
            wide: 10,
            big: 10,
            almond: 10,
            lashes: 8,
            sleepy: 8,
            narrow: 6,
            happy: 8,
            wink: 4,
            stars: 3,
            closed: 3,
            hearts: 2,
            dizzy: 1,
            sparkle: 5,
            'side-eye': 3,
            cyclops: 1,
          };
    case 'brows':
      return robot
        ? { none: 1 }
        : {
            none: 4,
            thin: 24,
            thick: 24,
            bushy: 10,
            arched: 15,
            angled: 10,
            worried: 7,
            unibrow: 4,
            raised: 5,
            dots: 3,
          };
    case 'nose':
      return robot
        ? { none: 8, dot: 1, button: 1 }
        : {
            none: 8,
            dot: 14,
            button: 20,
            round: 15,
            pointy: 10,
            long: 8,
            wide: 10,
            hook: 8,
            clown: 3,
            snout: 2,
            cat: 2,
          };
    case 'mouth':
      return robot
        ? { grille: 6, smile: 2, grin: 1, o: 1 }
        : {
            smile: 24,
            grin: 15,
            laugh: 8,
            teeth: 8,
            flat: 6,
            smirk: 12,
            o: 5,
            tongue: 6,
            buck: 5,
            fangs: 3,
            pout: 4,
            braces: 4,
            gap: 4,
            gum: 3,
            whistle: 2,
            mouthguard: 1,
          };
    case 'facial':
      return robot ? { none: 1 } : evenly(ids(FACE_FACIAL), 50, 5);
    case 'headwear':
      return evenly(ids(FACE_HEADWEAR), 30, 2);
    case 'eyewear':
      return evenly(ids(FACE_EYEWEAR), 60, 3);
    case 'neck':
      // What is worn at the neck, the garments being outfits now (§2.1).
      return evenly(
        FACE_NECK.filter((p) => !p.retired).map((p) => p.id),
        40,
        5,
      );
    case 'body':
      return robot
        ? { robot: 6, jersey: 2, overalls: 1, tee: 1 }
        : {
            jersey: 30,
            tee: 10,
            hoodie: 10,
            collar: 5,
            turtleneck: 4,
            overalls: 4,
            suit: 4,
            referee: 3,
            coach: 4,
            puffer: 4,
            tracksuit: 4,
            tank: 4,
            dress: 4,
            super: 3,
            robot: 1,
          };
    case 'held':
      return {
        none: 60,
        football: 8,
        'foam-finger': 6,
        pennant: 4,
        'pom-poms': 3,
        megaphone: 2,
        balloon: 3,
        popcorn: 3,
        soda: 3,
        clipboard: 2,
        teddy: 2,
        puppy: 2,
        flowers: 2,
        mug: 2,
        parrot: 1,
      };
  }
}

/**
 * The share of a category's shuffles a season's parts take between them
 * while it is on: a witch's hat a few times in a dozen in late October.
 */
const SEASON_SHARE: Partial<Record<FaceCategory, number>> = {
  headwear: 0.2,
  neck: 0.12,
  mouth: 0.08,
  marks: 0.2,
  body: 0.15,
  held: 0.2,
};

/** A category's weights on a day: a seasonal part only in its season, and then at its season's share. */
function inSeason(category: FaceCategory, weights: Weights, on: Date | undefined): Weights {
  const seasons = on ? faceSeasonsOn(on) : [];
  const out = { ...weights };
  const parts = FACE_CATEGORIES.find((c) => c.key === category)!.parts;
  const now = parts.filter(
    (p) => p.season !== undefined && !p.retired && seasons.includes(p.season),
  );
  for (const p of parts) if (p.season !== undefined) delete out[p.id];
  const total = Object.values(out).reduce((t: number, w) => t + (w ?? 0), 0);
  const share = SEASON_SHARE[category] ?? 0.1;
  for (const p of now) out[p.id] = (total * share) / (1 - share) / now.length;
  return out;
}

/** A few extras, or none; a theme's from its own, most of its faces wearing one. */
function randomMarks(rng: Rng, theme: FaceTheme = ANYTHING, on?: Date): FaceMark[] {
  const weights = (theme.marks ??
    inSeason('marks', Object.fromEntries(ids(FACE_MARKS).map((m) => [m, 1])), on)) as Partial<
    Record<FaceMark, number>
  >;
  const pool = Object.keys(weights).length;
  const n = Math.min(
    pool,
    Number(
      theme.marks
        ? weighted(rng, { '0': 15, '1': 50, '2': 35 })
        : weighted(rng, { '0': 45, '1': 35, '2': 20 }),
    ),
  );
  const out: FaceMark[] = [];
  while (out.length < n) {
    const m = weighted(rng, weights);
    if (!out.includes(m)) out.push(m);
  }
  return out;
}

/** Colors that go together: a person's skin nine times in ten, a hat in the team's colors half the time. */
function randomColors(rng: Rng, team: { primary: string; secondary: string } | null): FaceColors {
  const skin =
    rng() < 0.9
      ? pick(rng, SKIN_TONES.slice(0, SKIN_TONES_HUMAN)).hex
      : pick(rng, SKIN_TONES.slice(SKIN_TONES_HUMAN)).hex;
  const hair =
    rng() < 0.85
      ? pick(rng, HAIR_COLORS.slice(0, HAIR_COLORS_NATURAL)).hex
      : pick(rng, HAIR_COLORS.slice(HAIR_COLORS_NATURAL)).hex;
  const paint = () => pick(rng, PAINT_COLORS).hex;
  // IN THE TEAM'S COLORS, its ring's color on top: the disc behind the face is
  // the team's main color, and a hat in it would vanish into it.
  const teamed = team !== null && rng() < 0.5;
  const worn = team !== null && rng() < 0.5;
  return {
    skin,
    hair,
    brows: rng() < 0.9 ? hair : pick(rng, HAIR_COLORS).hex,
    facial: rng() < 0.85 ? hair : pick(rng, HAIR_COLORS).hex,
    eyes: pick(rng, EYE_COLORS.slice(0, 8)).hex,
    hat: teamed ? team.secondary : paint(),
    hatTrim: teamed ? darken(team.secondary, 0.7) : paint(),
    frames: pick(rng, [
      '#333333',
      '#111111',
      '#7a4a26',
      '#c9a227',
      '#1e66d0',
      '#e53935',
      '#c2185b',
    ]),
    lenses: '#cfe8ff',
    neck: worn ? team.secondary : paint(),
    neckTrim: worn ? '#ffffff' : paint(),
    paint: team !== null && rng() < 0.5 ? team.secondary : paint(),
  };
}

const ROBOT_METALS = ['#b9c6d8', '#c9ced6', '#9aa5b5', '#d4b46a', '#c98f6b', '#8fb8a8'];

/** The single-choice parts, in the order a shuffle picks them (the head first, which the rest look at). */
const PART_KEYS: readonly FacePartKey[] = [
  'head',
  'ears',
  'hair',
  'eyes',
  'brows',
  'nose',
  'mouth',
  'facial',
  'headwear',
  'eyewear',
  'neck',
];

/** How a shuffle shuffles: as a theme, keeping what is locked. */
export interface ShuffleOptions {
  /** Shuffle as this theme (`FACE_THEMES`); absent, as anything. */
  readonly theme?: FaceThemeId;
  /** Keep these categories of this face (`FACE_LOCKS`). */
  readonly keep?: { readonly face: Face; readonly locked: Iterable<FaceCategory> };
  /** Today, whose seasons' parts a shuffle wears now and then; absent, none. */
  readonly on?: Date;
}

/**
 * A whole new face, its hats and glasses in their own colors where they have
 * them; as a theme, in its parts and colors; keeping what is locked, and a
 * locked robot's head gets a robot's parts, as a robot's head drawn here does.
 */
export function randomFace(
  rng: Rng,
  team: { primary: string; secondary: string } | null = null,
  options: ShuffleOptions = {},
): Face {
  const theme = faceTheme(options.theme);
  const kept = options.keep?.face;
  const locked = new Set(kept ? options.keep!.locked : []);
  let face: Face = {
    head: 'round',
    ears: 'round',
    hair: 'short',
    eyes: 'round',
    brows: 'thin',
    nose: 'button',
    mouth: 'smile',
    facial: 'none',
    headwear: 'none',
    eyewear: 'none',
    neck: 'none',
    marks: [],
    colors: randomColors(rng, team),
    adjust: NO_ADJUST,
  };
  for (const key of PART_KEYS) {
    if (kept && locked.has(key)) {
      face = { ...face, [key]: kept[key] } as Face;
      continue;
    }
    const id = randomPart(rng, key, face, theme, options.on);
    // Half the time a choice keeps the shuffled colors rather than its own,
    // so not every crown is gold; a theme's always wears its own, so a
    // pirate's hat is a pirate's.
    const own = theme !== ANYTHING || rng() < 0.5;
    face = own ? chooseFacePart(face, key, id) : ({ ...face, [key]: id } as Face);
  }
  face = { ...face, marks: randomMarks(rng, theme, options.on) };
  // THE CHARACTER (§2.1): an outfit about half the time, always as a theme
  // that dresses its faces, most often the team's jersey; a number; and now
  // and then something to hold.
  if (theme.parts?.body !== undefined || rng() < 0.55)
    face = chooseFacePart(face, 'body', randomPart(rng, 'body', face, theme, options.on));
  face = { ...face, number: String(Math.floor(rng() * 100)) };
  face = chooseFacePart(face, 'held', randomPart(rng, 'held', face, theme, options.on));
  face = { ...face, colors: themeColors(rng, theme, team, face.colors) };
  // A robot is metal, whatever skin the shuffle drew.
  if (face.head === 'robot')
    face = { ...face, colors: { ...face.colors, skin: pick(rng, ROBOT_METALS) } };
  if (rng() < 0.3) {
    const tweak = (): number => Math.round(rng() * 2 - 1);
    face = { ...face, adjust: { ...NO_ADJUST, eyesY: tweak(), eyesGap: tweak(), mouthY: tweak() } };
  }
  return kept ? keepLocked(face, kept, locked) : face;
}

/**
 * The same face with one category shuffled, bringing the new choice's own
 * colors, as its theme where one is picked. Never the choice it had, so the
 * button always changes something. A lock does not hold it: shuffling the
 * hat is asking for another hat.
 */
export function shuffleFacePart(
  face: Face,
  category: FaceCategory,
  rng: Rng,
  options: Pick<ShuffleOptions, 'theme' | 'on'> = {},
): Face {
  const theme = faceTheme(options.theme);
  if (category === 'marks') {
    const same = (m: readonly FaceMark[]) =>
      m.length === face.marks.length && m.every((x) => face.marks.includes(x));
    let marks = randomMarks(rng, theme, options.on);
    for (let tries = 0; same(marks) && tries < 12; tries++)
      marks = randomMarks(rng, theme, options.on);
    // Where the theme's extras cannot differ, any extras.
    for (let tries = 0; same(marks) && tries < 12; tries++)
      marks = randomMarks(rng, ANYTHING, options.on);
    return { ...face, marks };
  }
  let id = randomPart(rng, category, face, theme, options.on);
  for (let tries = 0; id === face[category] && tries < 12; tries++)
    id = randomPart(rng, category, face, theme, options.on);
  // WHERE THE THEME OR THE HEAD ALLOWS ONLY THE CHOICE IT HAS (a robot theme's
  // head, a robot's brows, a monster's beard), any of a person's: the press
  // asked for another one, and the grid offers them all anyway.
  for (let tries = 0; id === face[category] && tries < 12; tries++)
    id = randomPart(rng, category, { head: 'round' }, ANYTHING, options.on);
  return chooseFacePart(face, category, id);
}

/** The same parts in new colors, as its theme where one is picked, keeping a locked category's. */
export function shuffleFaceColors(
  face: Face,
  rng: Rng,
  team: { primary: string; secondary: string } | null = null,
  options: Pick<ShuffleOptions, 'theme'> & { readonly locked?: Iterable<FaceCategory> } = {},
): Face {
  const theme = faceTheme(options.theme);
  let colors = themeColors(rng, theme, team, randomColors(rng, team));
  if (face.head === 'robot') colors = { ...colors, skin: pick(rng, ROBOT_METALS) };
  return keepLocked({ ...face, colors }, face, options.locked ?? []);
}

// ── Locks ──────────────────────────────────────────────────────────────────

/**
 * What a lock on a category keeps when the face is shuffled
 * (PLAN-2026-10-24-faces-round-two §1.1): its choice, the colors of the areas
 * it owns and its steps. Locked hair is this hair, color and all.
 */
export const FACE_LOCKS: Readonly<
  Record<
    FaceCategory,
    { readonly areas: readonly FaceArea[]; readonly steps: readonly FaceAdjustKey[] }
  >
> = {
  head: { areas: ['skin'], steps: [] },
  ears: { areas: [], steps: [] },
  hair: { areas: ['hair'], steps: [] },
  eyes: { areas: ['eyes'], steps: ['eyesY', 'eyesGap', 'eyesSize'] },
  brows: { areas: ['brows'], steps: ['browsY'] },
  nose: { areas: ['paint'], steps: ['noseY', 'noseSize'] },
  mouth: { areas: ['paint'], steps: ['mouthY', 'mouthSize'] },
  facial: { areas: ['facial'], steps: [] },
  headwear: { areas: ['hat', 'hatTrim'], steps: [] },
  eyewear: { areas: ['frames', 'lenses'], steps: [] },
  neck: { areas: ['neck', 'neckTrim'], steps: [] },
  marks: { areas: ['paint'], steps: [] },
  // The outfit keeps the number on it too (`keepLocked`).
  body: { areas: ['shirt', 'shirtTrim', 'pants', 'shoes'], steps: [] },
  held: { areas: ['held'], steps: [] },
};

/**
 * `next` with `face`'s locked categories put back: each one's choice, its
 * areas' colors and its steps. Brows and a beard that took the shuffled
 * hair's color take the kept hair's instead, as they follow it in the maker.
 */
export function keepLocked(next: Face, face: Face, locked: Iterable<FaceCategory>): Face {
  const keep = new Set(locked);
  if (keep.size === 0) return next;
  const out: Face = { ...next, colors: { ...next.colors }, adjust: { ...next.adjust } };
  // WHAT THE KEPT FACE LEAVES OUT IS LEFT OUT, not set to undefined: no
  // outfit, nothing held, an area left to the team.
  const put = (to: Record<string, unknown>, key: string, value: unknown) => {
    if (value === undefined) delete to[key];
    else to[key] = value;
  };
  for (const c of keep) {
    if (c === 'marks') out.marks = face.marks;
    else put(out, c, face[c]);
    if (c === 'body') put(out, 'number', face.number);
    for (const s of FACE_LOCKS[c].areas) put(out.colors, s, face.colors[s]);
    for (const k of FACE_LOCKS[c].steps) out.adjust[k] = face.adjust[k];
  }
  if (keep.has('hair')) {
    if (!keep.has('brows') && next.colors.brows === next.colors.hair)
      out.colors.brows = face.colors.hair;
    if (!keep.has('facial') && next.colors.facial === next.colors.hair)
      out.colors.facial = face.colors.hair;
  }
  return out;
}

// ── Mixing two faces ───────────────────────────────────────────────────────

/** How a mix mixes: the achievements its maker holds, so it never hands them a part they have not earned. */
export interface MixOptions {
  readonly earned?: readonly string[];
}

/**
 * A child of two faces (PLAN-2026-10-24-faces-round-three §6): each category
 * from one or the other at random, with that face's colors for the areas the
 * category owns and its steps (`FACE_LOCKS`), so hair taken from one face
 * brings that face's hair color; the extras from either. A part `b` wears
 * that its maker has not earned stays `a`'s. Both are read with their outfit
 * on the body (`wearOutfit`), so a garment worn at the neck mixes as an
 * outfit.
 */
export function mixFaces(a: Face, b: Face, rng: Rng, options: MixOptions = {}): Face {
  const one = wearOutfit(a);
  const other = wearOutfit(b);
  const earned = new Set(options.earned ?? []);
  const fromOther: FaceCategory[] = [];
  for (const c of FACE_CATEGORIES) {
    if (c.key === 'marks' || rng() < 0.5) continue;
    const id = other[c.key];
    const part = id === undefined ? undefined : facePart(c.key, id);
    if (part?.earnedBy !== undefined && !earned.has(part.earnedBy)) continue;
    fromOther.push(c.key);
  }
  const child = keepLocked(one, other, fromOther);
  // THE EXTRAS FROM EITHER: each of the two faces' extras now and then, up to four.
  const pool = [...new Set([...one.marks, ...other.marks])];
  const marks = pool.filter(() => rng() < 0.5).slice(0, FACE_MARKS_MAX);
  return { ...child, marks };
}

// ── Themes ─────────────────────────────────────────────────────────────────

export type FaceThemeId =
  | 'anything'
  | 'spooky'
  | 'festive'
  | 'superfan'
  | 'robot'
  | 'monster'
  | 'royal'
  | 'pirate'
  | 'wizard'
  | 'grandparent'
  | 'sleepy';

/** A color a theme paints with: six hex digits, or the team's own (`team` its main color, `ring` its second). */
type ThemeColor = string;

/**
 * Shuffling as something (PLAN-2026-10-24-faces-round-two §1.2): the same
 * shuffle with its own weights for the categories it has a say in, its own
 * extras and its own colors for some areas. Data beside the catalog, as the
 * shuffle's weights are, so a second client offers the same themes.
 */
export interface FaceTheme {
  readonly id: FaceThemeId;
  /** What the maker calls it: *A robot*. */
  readonly name: string;
  /** Its weights for the categories it has a say in; the rest are picked as any face's are. */
  readonly parts?: {
    readonly [K in FacePartKey]?: Readonly<Partial<Record<NonNullable<Face[K]>, number>>>;
  };
  /** The extras it draws from, by weight, most of its faces wearing one or two. */
  readonly marks?: Readonly<Partial<Record<FaceMark, number>>>;
  /** Its colors for the areas it has a say in, one picked for each. */
  readonly colors?: Readonly<Partial<Record<FaceArea, readonly ThemeColor[]>>>;
  /** Offered only in this season, its parts being the season's. */
  readonly season?: FaceSeasonId;
}

const hexOf = (list: readonly NamedColor[], ...names: string[]): string[] =>
  names.map((n) => {
    const c = list.find((x) => x.name === n);
    if (!c) throw new Error(`No color called ${n}`);
    return c.hex;
  });
const GRAY_HAIR = hexOf(HAIR_COLORS, 'Silver', 'Gray', 'Platinum');
/** The fantasy skins, less the robot's silver, which is a robot's. */
const FANTASY_SKIN = SKIN_TONES.slice(SKIN_TONES_HUMAN)
  .filter((c) => c.name !== 'Robot silver')
  .map((c) => c.hex);

const ANYTHING: FaceTheme = { id: 'anything', name: 'Anything' };

/** The themes, in the order the maker offers them, *Anything* first and a season's next. */
export const FACE_THEMES: readonly FaceTheme[] = [
  ANYTHING,
  {
    id: 'spooky',
    name: 'Spooky',
    season: 'halloween',
    parts: {
      ears: { pointy: 4, bolts: 2, round: 2, none: 1 },
      hair: { none: 2, messy: 2, spiky: 2, long: 2, 'flat-top': 2, mohawk: 1, wavy: 1 },
      eyes: { big: 2, wide: 3, dots: 2, narrow: 2, round: 2, stars: 1 },
      brows: { angled: 3, arched: 3, unibrow: 1, none: 1 },
      mouth: { fangs: 5, jagged: 5, grin: 2, o: 1, tongue: 1 },
      facial: { none: 6, goatee: 1 },
      headwear: {
        'witch-hat': 5,
        pumpkin: 4,
        'bat-headband': 4,
        horns: 2,
        'cat-ears': 2,
        hood: 1,
        none: 1,
      },
      eyewear: { none: 8, monocle: 1 },
      neck: { cape: 5, none: 2, neckerchief: 1 },
      body: { 'pumpkin-suit': 3, hoodie: 1, super: 1, tee: 1 },
      held: { 'candy-pail': 4, none: 2 },
    },
    marks: { web: 3, whiskers: 2, freckles: 1 },
    colors: { skin: [...FANTASY_SKIN, ...hexOf(SKIN_TONES, 'Porcelain')] },
  },
  {
    id: 'festive',
    name: 'Festive',
    season: 'winter',
    parts: {
      eyes: { happy: 4, round: 2, wide: 2, lashes: 1, sleepy: 1 },
      mouth: { smile: 3, grin: 3, laugh: 3, o: 1 },
      facial: { none: 5, full: 2, long: 1, mustache: 1 },
      headwear: { santa: 5, antlers: 4, elf: 4, 'pom-beanie': 2, beanie: 1 },
      neck: { scarf: 4, none: 3 },
      body: { sweater: 5, santa: 2, puffer: 2 },
      held: { present: 3, none: 2 },
    },
    marks: { blush: 4, snowflake: 3, freckles: 1 },
  },
  {
    id: 'superfan',
    name: 'A superfan',
    parts: {
      eyes: { wide: 3, big: 2, happy: 3, round: 2, wink: 1 },
      mouth: { grin: 3, laugh: 4, teeth: 2, o: 3, tongue: 1, mouthguard: 2 },
      facial: { none: 6, stubble: 1, goatee: 1 },
      headwear: {
        cap: 3,
        trucker: 2,
        'cap-back': 2,
        beanie: 2,
        'pom-beanie': 3,
        helmet: 2,
        visor: 1,
        headband: 2,
        bandana: 1,
        propeller: 1,
      },
      eyewear: { none: 8, sport: 1, shades: 1 },
      neck: { scarf: 3, none: 4, lanyard: 1, whistle: 1 },
      body: { jersey: 6, hoodie: 2, tee: 2, tank: 1 },
      held: { 'foam-finger': 4, pennant: 3, 'pom-poms': 2, football: 2, megaphone: 1, none: 1 },
    },
    marks: { 'eye-black': 4, stripes: 4, star: 2, heart: 1 },
    // The team's colors, its second on top: its main color is the disc
    // behind the face, and a cap in it would vanish into it.
    colors: {
      hat: ['ring'],
      hatTrim: ['team', '#ffffff'],
      neck: ['ring'],
      neckTrim: ['#ffffff', 'team'],
      paint: ['ring', 'team'],
    },
  },
  {
    id: 'robot',
    name: 'A robot',
    parts: { head: { robot: 1 } },
  },
  {
    id: 'monster',
    name: 'A monster',
    parts: {
      head: { round: 3, wide: 3, oval: 2, square: 2, heart: 1, long: 1 },
      ears: { pointy: 5, big: 3, floppy: 2, none: 1 },
      hair: { none: 4, messy: 3, spiky: 3, mohawk: 2, afro: 1, curly: 1 },
      eyes: { big: 3, wide: 3, dots: 2, stars: 1, round: 2, cyclops: 3 },
      brows: { unibrow: 3, bushy: 3, angled: 2, none: 2 },
      nose: { none: 3, dot: 2, wide: 2, round: 2, snout: 2 },
      mouth: { fangs: 5, teeth: 2, tongue: 3, grin: 2, laugh: 1, o: 1 },
      facial: { none: 1 },
      headwear: { horns: 5, antenna: 2, none: 4 },
      eyewear: { none: 6, goggles: 1 },
      neck: { none: 1 },
      body: { tee: 2, overalls: 2, hoodie: 1, tank: 1 },
      held: { none: 4, teddy: 1 },
    },
    marks: { freckles: 3, whiskers: 2, sweat: 1, bandage: 1 },
    colors: {
      skin: FANTASY_SKIN,
      hair: HAIR_COLORS.slice(HAIR_COLORS_NATURAL).map((c) => c.hex),
      eyes: hexOf(EYE_COLORS, 'Gold', 'Red glow', 'Cyan glow', 'Dark', 'Violet'),
    },
  },
  {
    id: 'royal',
    name: 'Royalty',
    parts: {
      eyes: { almond: 3, lashes: 3, round: 2, happy: 1, sleepy: 1 },
      brows: { arched: 4, thin: 3 },
      mouth: { smile: 3, smirk: 3, pout: 1 },
      facial: { none: 6, mustache: 1, handlebar: 2, goatee: 1 },
      headwear: { crown: 6, tiara: 4 },
      eyewear: { none: 8, monocle: 2 },
      neck: { pearls: 3, bowtie: 2, medal: 2, tie: 1 },
      body: { suit: 5, collar: 2, dress: 2 },
      held: { none: 4, flowers: 1 },
    },
    marks: { mole: 3, blush: 3, earrings: 3 },
  },
  {
    id: 'pirate',
    name: 'A pirate',
    parts: {
      hair: { long: 2, wavy: 2, braids: 2, locs: 2, messy: 2, curly: 1, bald: 1, ponytail: 1 },
      eyes: { narrow: 3, round: 2, wink: 2, wide: 1 },
      brows: { angled: 3, bushy: 3, thick: 2 },
      mouth: { grin: 3, teeth: 3, smirk: 3 },
      facial: { full: 3, long: 2, mustache: 2, handlebar: 2, stubble: 2, goatee: 1, none: 2 },
      headwear: { pirate: 6, bandana: 4 },
      eyewear: { patch: 5, none: 4, monocle: 1 },
      neck: { neckerchief: 3, none: 3 },
      body: { collar: 2, overalls: 1, tee: 1 },
      held: { parrot: 5, none: 3 },
    },
    marks: { earrings: 4, bandage: 2, sweat: 1 },
  },
  {
    id: 'wizard',
    name: 'A wizard',
    parts: {
      hair: { long: 3, wavy: 2, none: 2, bald: 2 },
      eyes: { sleepy: 2, round: 2, stars: 2, almond: 1, happy: 1 },
      brows: { bushy: 5, thick: 2 },
      mouth: { smile: 2, smirk: 2, o: 1, flat: 1 },
      facial: { long: 6, full: 3, mustache: 1 },
      headwear: { wizard: 8, hood: 2 },
      eyewear: { none: 4, round: 2, readers: 2, monocle: 1 },
      neck: { none: 4, scarf: 1 },
    },
    marks: { star: 2, blush: 1 },
    colors: { hair: [...GRAY_HAIR, '#ffffff'] },
  },
  {
    id: 'grandparent',
    name: 'A grandparent',
    parts: {
      hair: {
        bald: 4,
        buzz: 2,
        short: 2,
        side: 2,
        bun: 3,
        curly: 3,
        bob: 2,
        none: 1,
        'comb-over': 3,
      },
      eyes: { sleepy: 3, happy: 3, round: 2, lashes: 1, dots: 1 },
      brows: { bushy: 4, thick: 2, thin: 2 },
      nose: { round: 3, long: 2, button: 2, hook: 2, wide: 1 },
      mouth: { smile: 4, grin: 2, flat: 1, smirk: 1 },
      facial: { none: 5, mustache: 3, full: 1, chops: 1, walrus: 2 },
      headwear: {
        none: 6,
        beanie: 1,
        bucket: 1,
        cap: 1,
        'head-scarf': 1,
        newsboy: 1,
        'sun-hat': 1,
      },
      eyewear: { readers: 4, round: 3, big: 2, 'cat-eye': 2, square: 1, none: 1 },
      neck: { pearls: 3, scarf: 2, bowtie: 1, tie: 1, none: 2 },
      body: { turtleneck: 3, collar: 3, puffer: 1, overalls: 1, dress: 1 },
      held: { mug: 3, flowers: 1, none: 3 },
    },
    marks: { blush: 3, mole: 2, freckles: 1 },
    colors: { hair: GRAY_HAIR },
  },
  {
    id: 'sleepy',
    name: 'A sleepyhead',
    parts: {
      hair: { messy: 4, curly: 2, bun: 2, afro: 1, short: 1 },
      eyes: { sleepy: 6, closed: 4, happy: 1 },
      brows: { worried: 2, thin: 2, none: 1 },
      mouth: { o: 3, flat: 3, pout: 2, smile: 1 },
      facial: { none: 5, stubble: 2 },
      headwear: { 'sleep-mask': 4, beanie: 2, 'pom-beanie': 2, hood: 2, none: 3, earmuffs: 1 },
      neck: { scarf: 2, none: 4 },
      body: { hoodie: 4, puffer: 1, turtleneck: 2 },
      held: { teddy: 4, mug: 3, none: 2 },
    },
    marks: { blush: 2, sweat: 1 },
  },
];

/** The themes the maker offers on a day: every everyday one, and a season's while it is on. */
export function faceThemesOn(on: Date): FaceTheme[] {
  const seasons = faceSeasonsOn(on);
  return FACE_THEMES.filter((t) => t.season === undefined || seasons.includes(t.season));
}

/** A theme by id; *Anything* for none, or for one this catalog does not have. */
export function faceTheme(id: FaceThemeId | undefined): FaceTheme {
  return FACE_THEMES.find((t) => t.id === id) ?? ANYTHING;
}

/**
 * The theme's colors over the shuffled ones, for the areas it has a say in.
 * Brows and a beard that took the hair's color follow the theme's hair.
 */
function themeColors(
  rng: Rng,
  theme: FaceTheme,
  team: { primary: string; secondary: string } | null,
  colors: FaceColors,
): FaceColors {
  if (!theme.colors) return colors;
  const out = { ...colors };
  for (const slot of FACE_AREAS) {
    const list = theme.colors[slot]
      ?.map((c) => (c === 'team' ? team?.primary : c === 'ring' ? team?.secondary : c))
      .filter((c): c is string => c !== undefined);
    if (list && list.length > 0) out[slot] = pick(rng, list);
  }
  if (out.hair !== colors.hair) {
    if (!theme.colors.brows && colors.brows === colors.hair) out.brows = out.hair;
    if (!theme.colors.facial && colors.facial === colors.hair) out.facial = out.hair;
  }
  return out;
}

/** Two faces the same in every part, color and step: what a Save compares. */
export function sameFace(a: Face, b: Face): boolean {
  return JSON.stringify(Face.parse(a)) === JSON.stringify(Face.parse(b));
}

// ── In words ───────────────────────────────────────────────────────────────

/** The name of the palette color nearest a hex, from the palette its area draws from. */
export function colorName(hex: string, slot: FaceSlot = 'hat'): string {
  const rgb = (h: string) => {
    const n = Number.parseInt(h.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as const;
  };
  const [r, g, bl] = rgb(hex);
  let best = paletteFor(slot)[0]!;
  let far = Infinity;
  for (const c of paletteFor(slot)) {
    const [r2, g2, b2] = rgb(c.hex);
    const d = (r - r2) ** 2 + (g - g2) ** 2 + (bl - b2) ** 2;
    if (d < far) [best, far] = [c, d];
  }
  return best.name.toLowerCase();
}

/** A or an, by how the phrase starts aloud. */
const article = (phrase: string) => `${/^[aeiou]/i.test(phrase) ? 'an' : 'a'} ${phrase}`;
/** A part's name in a sentence: with an article unless it is a plural, as *freckles* is. */
const counted = (name: string) => (/s$/i.test(name) ? name : article(name));

/** How a part is said where its name alone does not say it in a sentence. */
const SAID: { readonly [K in FaceCategory]?: Readonly<Partial<Record<string, string>>> } = {
  head: { heart: 'heart-shaped' },
  eyes: {
    dots: 'dot eyes',
    lashes: 'eyes with lashes',
    narrow: 'squinting eyes',
    wink: 'a wink',
    stars: 'star eyes',
    leds: 'LED eyes',
    hearts: 'heart eyes',
    dizzy: 'spiral eyes',
    'side-eye': 'a side glance',
    cyclops: 'one big eye',
  },
  brows: {
    unibrow: 'a unibrow',
    raised: 'one brow raised',
    angled: 'stern brows',
    dots: 'dot brows',
  },
  nose: { clown: 'a clown nose', snout: 'a snout', cat: 'a cat nose' },
  mouth: {
    teeth: 'a toothy grin',
    flat: 'a straight mouth',
    o: 'an open mouth',
    tongue: 'a tongue out',
    buck: 'buck teeth',
    jagged: "a jack-o'-lantern grin",
    gap: 'a gap in the teeth',
    gum: 'a bubble of gum',
    whistle: 'a whistle on the lips',
  },
  facial: { handlebar: 'handlebar mustache', chops: 'sideburns' },
  held: { parrot: 'a parrot on the shoulder', 'pot-of-gold': 'a pot of gold in hand' },
  headwear: { 'shades-up': 'sunglasses up top' },
  eyewear: { '3d': '3D glasses' },
  marks: { blush: 'rosy cheeks', 'eye-black': 'eye black', mole: 'a beauty mark' },
};
/** The hair named as a look rather than a cut, said before the word *hair*. */
const HAIR_LOOKS = new Set(['short', 'spiky', 'messy', 'curly', 'long', 'wavy']);
/** Brows too ordinary to mention. */
const PLAIN_BROWS = new Set(['none', 'thin', 'thick']);
/** Ears said only when they are not a person's usual. */
const SAID_EARS = new Set(['big', 'pointy', 'floppy', 'bolts']);
/** What a neck part is called with no color before it, its name saying what it looks like. */
const UNCOLORED_NECK = new Set(['pearls', 'medal', 'chain', 'lanyard', 'whistle', 'overalls']);
/** Masses, said with no article: *stubble*, *eye black*. */
const MASS = new Set(['stubble', 'eye black', 'rosy cheeks']);

/**
 * A face said in a sentence (PLAN-2026-10-24-faces-round-two §4): *a round
 * face with short dark brown hair, wide eyes, a grin and a red cap*, made from
 * its parts' names and its colors' names, for a screen reader wherever a face
 * is read aloud. A second client says a face the same way.
 *
 * THE BADGE OR THE CHARACTER (§2.1): a badge is said as it is drawn, an
 * outfit only once it is chosen and with no number, and nothing held; the
 * character says the team's jersey it wears, the number and what is held.
 */
export function describeFace(face: Face, { character = false } = {}): string {
  const name = (category: FaceCategory, id: string) =>
    SAID[category]?.[id] ?? facePart(category, id)?.name.toLowerCase() ?? id;
  const said = (category: FaceCategory, id: string) => SAID[category]?.[id] !== undefined;
  // A fantasy skin is said, a person's and a robot's metal are not.
  const robot = face.head === 'robot';
  const skin =
    !robot && SKIN_TONES.slice(SKIN_TONES_HUMAN).some((c) => c.hex === face.colors.skin)
      ? `${colorName(face.colors.skin, 'skin')} `
      : '';
  const head = article(`${skin}${name('head', face.head)} face`);
  const parts: string[] = [];

  if (SAID_EARS.has(face.ears))
    parts.push(face.ears === 'bolts' ? 'bolts for ears' : `${name('ears', face.ears)} ears`);

  const hair = colorName(face.colors.hair, 'hair');
  if (face.hair === 'none') {
    if (!robot) parts.push('no hair');
  } else if (face.hair === 'bald') parts.push(`a horseshoe of ${hair} hair`);
  else if (HAIR_LOOKS.has(face.hair)) parts.push(`${name('hair', face.hair)} ${hair} hair`);
  else parts.push(counted(`${hair} ${name('hair', face.hair)}`));

  parts.push(said('eyes', face.eyes) ? name('eyes', face.eyes) : `${name('eyes', face.eyes)} eyes`);
  if (!PLAIN_BROWS.has(face.brows))
    parts.push(
      said('brows', face.brows) ? name('brows', face.brows) : `${name('brows', face.brows)} brows`,
    );
  if (said('nose', face.nose)) parts.push(name('nose', face.nose));
  parts.push(
    said('mouth', face.mouth) ? name('mouth', face.mouth) : counted(name('mouth', face.mouth)),
  );

  if (face.facial !== 'none') {
    const beard = `${colorName(face.colors.facial, 'facial')} ${name('facial', face.facial)}`;
    parts.push(face.facial === 'stubble' ? beard : counted(beard));
  }
  if (face.headwear !== 'none')
    parts.push(
      said('headwear', face.headwear)
        ? name('headwear', face.headwear)
        : counted(`${colorName(face.colors.hat)} ${name('headwear', face.headwear)}`),
    );
  if (face.eyewear !== 'none') parts.push(counted(name('eyewear', face.eyewear)));
  if (face.neck !== 'none')
    parts.push(
      UNCOLORED_NECK.has(face.neck)
        ? counted(name('neck', face.neck))
        : counted(`${colorName(face.colors.neck)} ${name('neck', face.neck)}`),
    );
  for (const m of face.marks) {
    const mark = name('marks', m);
    parts.push(MASS.has(mark) || said('marks', m) ? mark : counted(mark));
  }
  // THE CHARACTER (§2.1): an outfit chosen, in its color where it is painted
  // (a garment at the neck is said with the neck); the team's jersey only for
  // its number; and what is held.
  const outfit = faceOutfit(face);
  const numbered = character && NUMBERED_OUTFITS.has(outfit.id) && face.number !== undefined;
  const number = numbered ? ` number ${face.number}` : '';
  if (outfit.from === 'body') {
    const color = face.colors.shirt ? `${colorName(face.colors.shirt)} ` : '';
    parts.push(`${counted(`${color}${name('body', outfit.id)}`)}${number}`);
  } else if (outfit.from === 'team' && character)
    parts.push(outfit.id === 'robot' ? "a robot's body" : `the team's jersey${number}`);
  if (character && face.held !== undefined && face.held !== 'none')
    parts.push(
      said('held', face.held)
        ? name('held', face.held)
        : `${counted(name('held', face.held))} in hand`,
    );

  const list =
    parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  return list ? `${head} with ${list}` : head;
}
